<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="a"
                :pages="[
                    {
                        name: $tc('messages.meeting_turns', 2),
                        href: meta.links.index,
                    },
                    {
                        name: $t('messages.create'),
                        href: null,
                    },
                ]"
            />
        </template>

        <TitleFieldsFormSection>
            <template v-slot:title>
                <div>
                    {{ $t('messages.date') }}

                    <span v-if="!date">
                        <button @click="setDateToToday()" class="text-xs text-indigo-400" type="button">
                            {{ $t('messages.today?') }}
                        </button>
                    </span>
                </div>
                <form @submit.prevent="addDay">
                    <div class="flex gap-x-2 items-center">
                        <div>
                            <FormInput required v-model="date" name="date" type="date" class="mt-1" />
                        </div>

                        <PlusButton type="submit" />
                    </div>
                </form>
            </template>
            <form :action="meta.links.update" method="post" @submit.prevent="submit">
                <AppWell>
                    <div v-auto-animate class="">
                        <div class="mt-3" v-for="(day, index) in form.days" :key="day.key">
                            <AppCard>
                                <div class="p-2">
                                    <div class="flex justify-between">
                                        <div class="mt-1">
                                            <FormLabel :value="$t('messages.date')" for="date" />
                                            <FormInput
                                                required
                                                id="date"
                                                v-model="day.date"
                                                type="date"
                                                name="date"
                                                class="mt-1"
                                            />
                                            <FormError :value="form.errors[`days.${index}.date`]" class="mt-2" />
                                        </div>
                                        <div>
                                            <button
                                                type="button"
                                                :title="$t('messages.remove')"
                                                @click="duplicateRow(day)"
                                            >
                                                <IconCopy class="text-gray-400 pr-1" />
                                            </button>
                                            <button
                                                type="button"
                                                :title="$t('messages.remove')"
                                                @click="deleteRow(index)"
                                            >
                                                <IconTrash class="text-gray-400 pr-1" />
                                            </button>
                                        </div>
                                    </div>

                                    <div class="md:grid md:grid-cols-12 md:gap-6">
                                        <div class="sm:col-span-4">
                                            <div class="mt-1">
                                                <FormLabel
                                                    :value="$t('messages.create_meetings_starting_from_time')"
                                                    for="start_time"
                                                />
                                                <FormInput
                                                    required
                                                    id="start_time"
                                                    v-model="day.start_time"
                                                    name="start_time"
                                                    type="time"
                                                    class="mt-1"
                                                />
                                                <FormError
                                                    :value="form.errors[`days.${index}.start_time`]"
                                                    class="mt-2"
                                                />
                                            </div>
                                        </div>
                                        <div class="sm:col-span-4">
                                            <div class="mt-1">
                                                <FormLabel
                                                    :value="$t('messages.quantity_of_turns_to_create')"
                                                    for="quantity"
                                                />
                                                <FormInput
                                                    required
                                                    id="quantity"
                                                    v-model="day.quantity"
                                                    name="quantity"
                                                    type="number"
                                                    step="1"
                                                    min="1"
                                                    max="200"
                                                    class="mt-1 w-full"
                                                />
                                                <FormError
                                                    :value="form.errors[`days.${index}.quantity`]"
                                                    class="mt-2"
                                                />
                                            </div>
                                        </div>
                                        <div class="sm:col-span-4">
                                            <div class="mt-1">
                                                <FormLabel :value="$t('messages.duration_of_turn')" for="duration" />
                                                <FormInput
                                                    required
                                                    id="duration"
                                                    v-model="day.duration"
                                                    name="duration"
                                                    type="number"
                                                    step="1"
                                                    min="1"
                                                    max="360"
                                                    class="mt-1 w-full"
                                                />
                                                <FormError
                                                    :value="form.errors[`days.${index}.duration`]"
                                                    class="mt-2"
                                                />
                                            </div>
                                        </div>

                                        <div class="sm:col-span-12">
                                            <div class="mt-1">
                                                <FormLabel :value="$t('messages.groups')" for="group_ids" />
                                                <div class="mt-1 rounded-md shadow-sm">
                                                    <label
                                                        class="ml-1 mr-3"
                                                        v-for="group in options.groups"
                                                        :key="group.value"
                                                    >
                                                        <input
                                                            name="group_ids[]"
                                                            type="checkbox"
                                                            :value="group.value"
                                                            v-model="day.group_ids"
                                                            class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                                        />
                                                        <span
                                                            class="inline-block text-sm leading-5 font-medium text-gray-700"
                                                        >
                                                            {{ group.text }}
                                                        </span>
                                                    </label>
                                                </div>
                                                <FormError
                                                    :value="form.errors[`days.${index}.group_ids`]"
                                                    class="mt-2"
                                                />
                                            </div>
                                        </div>

                                        <div class="sm:col-span-6 hidden">
                                            <div class="mt-1">
                                                <FormLabel :value="$t('messages.comment')" for="comment" />
                                                <FormInput
                                                    id="comment"
                                                    v-model="form.comment"
                                                    name="comment"
                                                    type="text"
                                                    class="mt-1"
                                                />
                                                <FormError :value="form.errors.comment" class="mt-2" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </AppCard>
                        </div>
                    </div>
                </AppWell>

                <div class="flex justify-end mt-5">
                    <AppButton desired-action element="button" type="submit" :disabled="form.processing">
                        {{ $t('messages.save') }}
                        <span v-if="form.processing" class="ml-1 btn-spinner"></span>
                    </AppButton>
                </div>
            </form>
        </TitleFieldsFormSection>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import TitleFieldsFormSection from '@/components/TitleFieldsFormSection.vue';
import FormInput from '@/components/Forms/FormInput.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormError from '@/components/Forms/FormError.vue';
import AppButton from '@/components/AppButton.vue';
import AppWell from '@/Shared/AppWell.vue';
import PlusButton from '@/components/Forms/PlusButton.vue';
import AppCard from '@/Shared/AppCard.vue';
import { IconCopy, IconTrash } from '@tabler/icons-vue';

export default {
    components: {
        AppCard,
        PlusButton,
        AppWell,
        PanelPage,
        TitleFieldsFormSection,
        FormInput,
        FormLabel,
        FormError,
        AppButton,
        IconTrash,
        IconCopy,
    },
    layout: Layout,
    name: 'Create',
    props: ['meta', 'options'],
    data() {
        return {
            form: this.$inertia.form({
                days: [],
            }),
            date: new Date().toISOString().slice(0, 10),
            counter: 0,
        };
    },
    methods: {
        submit() {
            this.form.post(this.meta.links.store);
        },
        addDay() {
            this.counter++;
            this.form.days.push({
                date: this.date,
                start_time: null,
                quantity: null,
                duration: null,
                comment: null,
                group_ids: [],
                key: this.counter,
            });
        },
        deleteRow(index) {
            this.form.days.splice(index, 1);
        },
        duplicateRow(day) {
            this.counter++;
            this.form.days.push({
                ...day,
                key: this.counter,
            });
        },
        setDateToToday() {
            this.date = new Date().toISOString().slice(0, 10);
        },
    },
};
</script>
